import { useEffect, useRef, useState } from 'react';

/**
 * In-app Android scroll diagnostic overlay.
 *
 * Why it exists:
 *   The Android-only scroll bug ("centre-screen swipe doesn't scroll, but the
 *   bottom-nav area does") cannot be reproduced in the preview / Chrome.  To
 *   triage it on a real device without USB debugging, you can open the app
 *   with `?zsdebug=touch` appended to any route — this overlay mounts and
 *   captures every datapoint the bug report asks for (elementFromPoint at
 *   4 screen positions, root scroll ownership, every touchstart/touchmove/
 *   touchend with deltaY + scrollY) and renders the most recent ~80 lines on
 *   screen so you can read it without chrome://inspect.
 *
 *   Activation: append `?zsdebug=touch` to ANY URL on the deployed Android
 *   app.  The overlay is dismissed with the X button.  It is **never**
 *   mounted unless the query param is set, so it ships safely to production.
 */
function pickPoints() {
  const w = window.innerWidth;
  const h = window.innerHeight;
  return [
    { name: 'top',        x: w / 2, y: Math.round(h * 0.20) },
    { name: 'center',     x: w / 2, y: Math.round(h * 0.50) },
    { name: 'lower',      x: w / 2, y: Math.round(h * 0.75) },
    { name: 'bottom-nav', x: w / 2, y: Math.round(h * 0.90) },
  ];
}

function describeElement(el) {
  if (!el) return null;
  const cs = getComputedStyle(el);
  const r = el.getBoundingClientRect();
  return {
    tag: el.tagName,
    id: el.id || '',
    cls: (el.className && el.className.toString) ? el.className.toString().slice(0, 100) : '',
    testid: el.getAttribute && el.getAttribute('data-testid') || '',
    pointerEvents: cs.pointerEvents,
    touchAction: cs.touchAction,
    overflowY: cs.overflowY,
    position: cs.position,
    z: cs.zIndex,
    rect: { x: Math.round(r.x), y: Math.round(r.y), w: Math.round(r.width), h: Math.round(r.height) },
    text: (el.innerText || '').slice(0, 60),
  };
}

function rootInfo() {
  return {
    scrollingElement: document.scrollingElement?.tagName,
    scrollY: window.scrollY,
    docScrollHeight: document.documentElement.scrollHeight,
    bodyScrollHeight: document.body.scrollHeight,
    innerHeight: window.innerHeight,
    innerWidth: window.innerWidth,
    htmlOverflowY: getComputedStyle(document.documentElement).overflowY,
    bodyOverflowY: getComputedStyle(document.body).overflowY,
    rootOverflowY: (() => {
      const r = document.getElementById('root');
      return r ? getComputedStyle(r).overflowY : 'n/a';
    })(),
    htmlOverscroll: getComputedStyle(document.documentElement).overscrollBehaviorY,
    bodyOverscroll: getComputedStyle(document.body).overscrollBehaviorY,
    capacitor: (() => {
      try {
        const c = window.Capacitor;
        if (!c) return null;
        return {
          platform: c.getPlatform && c.getPlatform(),
          isNative: typeof c.isNativePlatform === 'function' ? c.isNativePlatform() : !!c.isNative,
        };
      } catch { return null; }
    })(),
    url: window.location.href,
    userAgent: navigator.userAgent,
  };
}

export default function AndroidScrollDiagnostic() {
  // Only mount when ?zsdebug=touch is present.  Avoids any production overhead.
  const enabled = (() => {
    try {
      return new URLSearchParams(window.location.search).get('zsdebug') === 'touch';
    } catch { return false; }
  })();

  const [open, setOpen] = useState(enabled);
  const [lines, setLines] = useState([]);
  const lastYRef = useRef(null);
  const buffRef = useRef([]);

  useEffect(() => {
    if (!enabled || !open) return undefined;

    const push = (label, payload) => {
      const stamp = new Date().toISOString().slice(11, 23);
      const line = `[${stamp}] ${label} ${JSON.stringify(payload)}`;
      buffRef.current.push(line);
      // Keep the last 80 lines so the UI never balloons.
      if (buffRef.current.length > 80) buffRef.current.shift();
      setLines([...buffRef.current]);
    };

    // 1. Initial snapshot
    push('ROOT', rootInfo());
    push('POINTS', pickPoints().map((p) => ({
      name: p.name, x: p.x, y: p.y,
      hit: describeElement(document.elementFromPoint(p.x, p.y)),
    })));

    // 2. Real touch diagnostics — passive so we never block scrolling
    const onStart = (event) => {
      const t = event.touches[0];
      if (!t) return;
      lastYRef.current = t.clientY;
      push('TOUCHSTART', {
        x: Math.round(t.clientX), y: Math.round(t.clientY),
        targetTag: event.target?.tagName,
        targetCls: (event.target?.className?.toString?.() || '').slice(0, 80),
        elFromPoint: describeElement(document.elementFromPoint(t.clientX, t.clientY)),
        scrollY: window.scrollY,
        bodyScrollTop: document.body.scrollTop,
        docScrollTop: document.documentElement.scrollTop,
      });
    };
    const onMove = (event) => {
      const t = event.touches[0];
      if (!t) return;
      const dy = lastYRef.current == null ? 0 : t.clientY - lastYRef.current;
      lastYRef.current = t.clientY;
      // Throttle move events to once per ~50 ms so the log is readable.
      const now = Date.now();
      if (now - (onMove._lastT || 0) < 50) return;
      onMove._lastT = now;
      push('TOUCHMOVE', {
        y: Math.round(t.clientY), dy: Math.round(dy),
        scrollY: window.scrollY,
        bodyScrollTop: document.body.scrollTop,
        docScrollTop: document.documentElement.scrollTop,
      });
    };
    const onEnd = () => {
      push('TOUCHEND', {
        scrollY: window.scrollY,
        bodyScrollTop: document.body.scrollTop,
        docScrollTop: document.documentElement.scrollTop,
      });
      lastYRef.current = null;
    };

    window.addEventListener('touchstart', onStart, { passive: true });
    window.addEventListener('touchmove', onMove, { passive: true });
    window.addEventListener('touchend', onEnd, { passive: true });
    return () => {
      window.removeEventListener('touchstart', onStart);
      window.removeEventListener('touchmove', onMove);
      window.removeEventListener('touchend', onEnd);
    };
  }, [enabled, open]);

  if (!enabled || !open) return null;

  const copy = async () => {
    const txt = ['=== ZS Android Scroll Diagnostic ===',
      JSON.stringify(rootInfo(), null, 2),
      '--- POINTS ---',
      JSON.stringify(pickPoints().map((p) => ({
        name: p.name,
        hit: describeElement(document.elementFromPoint(p.x, p.y)),
      })), null, 2),
      '--- EVENTS ---',
      ...buffRef.current,
    ].join('\n');
    try {
      await navigator.clipboard.writeText(txt);
      alert('Copied diagnostic log to clipboard.');
    } catch {
      // Fallback for Android WebView clipboard restrictions.
      const ta = document.createElement('textarea');
      ta.value = txt;
      ta.style.position = 'fixed';
      ta.style.top = '0';
      ta.style.left = '0';
      ta.style.opacity = '0';
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
      alert('Copied diagnostic log (fallback).');
    }
  };

  // Render OUTSIDE the page's z-index stack so it can't be hidden by other
  // overlays.  pointer-events restricted to the panel itself so touches
  // that miss the panel still hit the page (so the user can scroll while
  // diagnostics record).
  return (
    <div
      data-testid="zs-android-diagnostic"
      className="fixed inset-x-2 bottom-2 z-[2147483647]"
      style={{ pointerEvents: 'none' }}
    >
      <div
        className="rounded-xl border border-[#FFD166] bg-black/85 backdrop-blur-md text-[10px] leading-tight text-[#A7F3D0] p-2 font-mono"
        style={{ pointerEvents: 'auto', maxHeight: '38vh', overflowY: 'auto' }}
      >
        <div className="flex items-center justify-between gap-2 sticky top-0 bg-black/85 pb-1 mb-1 border-b border-white/10">
          <span className="text-[#FFD166] font-bold">ZS Android Diagnostic</span>
          <div className="flex gap-2">
            <button onClick={copy} className="px-2 py-0.5 rounded bg-[#1C4749] text-white text-[10px]">Copy</button>
            <button onClick={() => setOpen(false)} className="px-2 py-0.5 rounded bg-white/10 text-white text-[10px]">X</button>
          </div>
        </div>
        {lines.length === 0 ? (
          <div className="text-white/60">Swipe anywhere on the page — events will stream below.</div>
        ) : (
          lines.slice().reverse().map((l, i) => (
            <div key={`${lines.length - 1 - i}:${l.slice(0, 32)}`} className="whitespace-pre-wrap break-all">{l}</div>
          ))
        )}
      </div>
    </div>
  );
}
